/**
 * CostSchemeList — the flat, day-by-day list view of the Cost Scheme agenda (the alternative to the board).
 * Groups parts under a card per day; within each day it lists accommodation, then multi-day services, then
 * timed activities (each sorted). Prices are editable inline; clicking a row opens the drawer. Read-only
 * layouts (inherited term) disable the inline price inputs.
 */
import { useTranslation } from 'react-i18next';

import { fmtH, type BoardModel, type BoardPart } from '../model';
import { ListRow } from './ListRow';

interface CostSchemeListProps {
  model: BoardModel;
  readOnly: boolean;
  onOpenPart: (uid: string) => void;
  onUpdatePart: (uid: string, patch: Partial<BoardPart>) => void;
}

export function CostSchemeList({ model, readOnly, onOpenPart, onUpdatePart }: CostSchemeListProps) {
  const { t } = useTranslation('venua-planner');
  const days = Array.from({ length: model.lengthDays }, (_, i) => i);

  const accommodation = model.parts.filter((p) => p.kind === 'accommodation');
  const services = model.parts.filter((p) => p.kind === 'service');
  const timed = model.parts.filter((p) => p.kind === 'timed');

  return (
    <div className="tp-list">
      <div className="tp-list-inner">
        {days.map((dayIdx) => {
          // An accommodation/service part shows on every day it covers; timed parts only on their own day.
          const dayAccoms = accommodation
            .filter((a) => dayIdx >= a.day && dayIdx < a.day + a.span)
            .sort((a, b) => a.day - b.day);
          const dayServices = services
            .filter((s) => dayIdx >= s.day && dayIdx < s.day + s.span)
            .sort((a, b) => (a.row ?? 0) - (b.row ?? 0) || a.day - b.day);
          const dayTimed = timed
            .filter((p) => p.day === dayIdx)
            .sort((a, b) => (a.startHour ?? 0) - (b.startHour ?? 0) || a.hours - b.hours);
          const count = dayAccoms.length + dayServices.length + dayTimed.length;

          return (
            <section key={dayIdx} className="tp-list-day">
              <header className="tp-list-dayhead">
                <span className="tp-list-daydow">{t('costScheme.dayShort')}</span>
                <span className="tp-list-daydate">{dayIdx + 1}</span>
                <span className="tp-list-dayname">
                  {t('costScheme.day')} {dayIdx + 1}
                </span>
                <span className="tp-list-daycount">
                  <span className="font-mono tabular-nums">{count}</span> {t('costScheme.items')}
                </span>
              </header>
              <div className="tp-list-rows">
                {count === 0 ? (
                  <div className="tp-list-empty">{t('costScheme.dayEmpty')}</div>
                ) : null}
                {dayAccoms.map((a) => {
                  const pos = dayIdx - a.day + 1;
                  const hint = [a.facilitySubTypeName, a.accommodationCategoryName]
                    .filter(Boolean)
                    .join(' · ');
                  const sub = `${t('costScheme.types.Accommodation')}${a.span > 1 ? ` · ${t('costScheme.night')} ${pos}/${a.span}` : ''}${hint ? ` · ${hint}` : ''}`;
                  return (
                    <ListRow
                      key={`acc-${a.uid}`}
                      t={t}
                      part={a}
                      readOnly={readOnly}
                      tag={t('costScheme.nightTag')}
                      sub={sub}
                      onOpen={onOpenPart}
                      onUpdatePart={onUpdatePart}
                    />
                  );
                })}
                {dayServices.map((s) => {
                  const pos = dayIdx - s.day + 1;
                  const sub = `${t(`costScheme.types.${s.type}`)}${s.span > 1 ? ` · ${t('costScheme.day')} ${pos}/${s.span}` : ''}`;
                  return (
                    <ListRow
                      key={`svc-${s.uid}`}
                      t={t}
                      part={s}
                      readOnly={readOnly}
                      tag={t('costScheme.dayTag')}
                      sub={sub}
                      onOpen={onOpenPart}
                      onUpdatePart={onUpdatePart}
                    />
                  );
                })}
                {dayTimed.map((p) => {
                  const start = p.startHour ?? 0;
                  const sub = `${t(`costScheme.types.${p.type}`)} · ${p.hours}h`;
                  return (
                    <ListRow
                      key={p.uid}
                      t={t}
                      part={p}
                      readOnly={readOnly}
                      sub={sub}
                      timeMain={fmtH(start)}
                      timeSub={fmtH(start + p.hours)}
                      timeDur={`${p.hours}h`}
                      onOpen={onOpenPart}
                      onUpdatePart={onUpdatePart}
                    />
                  );
                })}
              </div>
            </section>
          );
        })}
      </div>
    </div>
  );
}
